import {
  FiPlay,
  FiEdit2,
  FiTrash2,
  FiClock,
  FiChevronDown,
  FiSave,
  FiCalendar,
  FiActivity,
  FiLayers,
  FiInfo,
  FiPlus,
  FiX,
  FiGrid,
  FiImage,
  FiDatabase,
} from "react-icons/fi";
import { useContext, useState, useEffect } from "react";
import { BaseContext } from "../../../contexts/BaseContext";
import { deasyWorkflowService } from "../../../services/api";
import { WorkflowJobs } from "./Jobs/WorkflowJob";
import { getStatusClass, formatCronExpression } from "./utils";
import { toast } from "react-hot-toast";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Available job types for the job selector
const JOB_TYPES = [
  {
    endpoint: "/ocr/ingest",
    name: "Unstructured Data Synchronization (OCR)",
    icon: <FiImage className="w-4 h-4" />,
    defaultBody: {
      data_connector_name: "",
      use_llm: false,
    },
  },
  {
    endpoint: "/prepare_data",
    name: "Deasy Data Preparation",
    icon: <FiDatabase className="w-4 h-4" />,
    defaultBody: {
      data_connector_name: "",
      llm_profile_name: "Deasy Compute",
    },
  },
  {
    endpoint: "/classify_bulk",
    name: "Classification",
    icon: <FiGrid className="w-4 h-4" />,
    defaultBody: {
      data_connector_name: "",
      llm_profile_name: "Deasy Compute",
      tag_names: [],
    },
  },
  {
    endpoint: "/dataslice/export/metadata/destination",
    name: "Export Metadata",
    icon: <FiDatabase className="w-4 h-4" />,
    defaultBody: {
      dataslice_id: "",
      data_connector_name: "",
      destination_name: "",
      export_tags: [],
      export_level: "BOTH",
    },
  },
];

export const DeasyWorkflow = ({
  workflow,
  setSelectedWorkflow,
  setOpenDialog,
  setOpenDeleteDialog,
  fetchWorkflows,
  onEditWorkflow,
}) => {
  const { deasyApiKey } = useContext(BaseContext);
  const [editableWorkflow, setEditableWorkflow] = useState(workflow);
  const [showJobSelector, setShowJobSelector] = useState(null); // Stores the stage index when selector is shown

  // Ensure editableWorkflow stays in sync when the workflow prop changes
  useEffect(() => {
    setEditableWorkflow(workflow);
  }, [workflow]);

  const handleEditWorkflow = () => {
    if (onEditWorkflow) {
      onEditWorkflow(editableWorkflow);
    } else {
      setSelectedWorkflow(workflow);
      setOpenDialog(true);
    }
  };

  const handleDeleteWorkflow = () => {
    setSelectedWorkflow(workflow);
    setOpenDeleteDialog(true);
  };

  const handleRunWorkflow = async () => {
    try {
      deasyWorkflowService.manualTriggerDeasyWorkflow(deasyApiKey, workflow.id);

      // Show toast notification
      toast.success(
        <div>
          <p className="font-bold">{workflow.name} triggered successfully!</p>
          <p className="text-sm mt-1">
            Refresh and check workflow history for up-to-date tracking.
          </p>
        </div>,
        {
          duration: 5000,
          icon: "🔄",
          style: {
            borderRadius: "10px",
            background: "#f3f4f6",
            color: "#111827",
          },
        },
      );

      fetchWorkflows();
    } catch (error) {
      console.error("Failed to run workflow:", error);
      toast.error(
        <div>
          <p className="font-bold">Failed to trigger workflow</p>
          <p className="text-sm mt-1">Please try again later.</p>
        </div>,
        {
          duration: 4000,
          icon: "❌",
        },
      );
    }
  };

  const handleSaveWorkflow = async () => {
    try {
      // Call API to update the workflow
      await deasyWorkflowService.upsertDeasyWorkflow(
        deasyApiKey,
        editableWorkflow,
      );

      // Refresh workflows from server to ensure UI is updated
      await fetchWorkflows();

      // Show visual feedback (optional)
      const saveButton = document.getElementById(
        `save-workflow-${editableWorkflow.id}`,
      );
      if (saveButton) {
        saveButton.classList.add("bg-green-500");
        setTimeout(() => {
          saveButton.classList.remove("bg-green-500");
        }, 1000);
      }
    } catch (error) {
      console.error("Failed to save workflow:", error);
    }
  };

  // Add a new stage to the workflow
  const handleAddStage = () => {
    const newStage = {
      status: "pending",
      jobs: [],
    };

    setEditableWorkflow({
      ...editableWorkflow,
      stages: [...(editableWorkflow.stages || []), newStage],
    });
  };

  // Add a new job to a specific stage
  const handleAddJob = (stageIndex, jobType) => {
    const newJob = {
      id: `temp-${Date.now()}`, // Temporary ID until saved
      endpoint: jobType.endpoint,
      endpoint_request_body: jobType.defaultBody,
      status: "pending",
    };

    const updatedStages = [...(editableWorkflow.stages || [])];
    if (!updatedStages[stageIndex]) {
      updatedStages[stageIndex] = { status: "pending", jobs: [] };
    }
    updatedStages[stageIndex].jobs = [
      ...(updatedStages[stageIndex].jobs || []),
      newJob,
    ];

    setEditableWorkflow({
      ...editableWorkflow,
      stages: updatedStages,
    });

    setShowJobSelector(null);
  };

  // Remove a job from a stage
  const handleRemoveJob = (stageIndex, jobIndex) => {
    const updatedStages = [...(editableWorkflow.stages || [])];
    if (updatedStages[stageIndex] && updatedStages[stageIndex].jobs) {
      updatedStages[stageIndex].jobs.splice(jobIndex, 1);
    }

    setEditableWorkflow({
      ...editableWorkflow,
      stages: updatedStages,
    });
  };

  // Remove an entire stage
  const handleRemoveStage = (stageIndex) => {
    const updatedStages = [...(editableWorkflow.stages || [])];
    updatedStages.splice(stageIndex, 1);

    setEditableWorkflow({
      ...editableWorkflow,
      stages: updatedStages,
    });
  };

  // Update a job in a specific stage
  const handleJobUpdate = (updatedJob, jobIndex, stageIndex) => {
    const updatedStages = [...(editableWorkflow.stages || [])];
    if (updatedStages[stageIndex] && updatedStages[stageIndex].jobs) {
      updatedStages[stageIndex].jobs[jobIndex] = updatedJob;
    }

    setEditableWorkflow({
      ...editableWorkflow,
      stages: updatedStages,
    });
  };

  // Action button component for consistent styling
  const ActionButton = ({
    icon,
    onClick,
    title,
    disabled = false,
    danger = false,
    id,
  }) => (
    <button
      className={`p-2.5 rounded-lg text-gray-600 transition-all flex items-center justify-center
        ${
          danger
            ? "hover:text-red-500 hover:bg-red-500/10"
            : "hover:text-primary hover:bg-primary/10"
        }
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
      `}
      title={title}
      onClick={onClick}
      disabled={disabled}
      id={id}
    >
      {icon}
    </button>
  );

  // Job type selector component
  const JobTypeSelector = ({ stageIndex }) => (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-3 mt-3">
      <div className="flex justify-between items-center mb-3">
        <h5 className="font-medium text-gray-700">Select Job Type</h5>
        <button
          onClick={() => setShowJobSelector(null)}
          className="text-gray-500 hover:text-gray-700"
        >
          <FiX className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {JOB_TYPES.map((jobType) => (
          <button
            key={jobType.endpoint}
            className="flex items-center p-3 border border-gray-200 rounded-lg bg-white hover:border-primary hover:bg-primary/5 transition-all"
            onClick={() => handleAddJob(stageIndex, jobType)}
          >
            <div className="p-2 bg-primary/10 rounded-md text-primary mr-3">
              {jobType.icon}
            </div>
            <span>{jobType.name}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:border-primary/40 hover:shadow-lg transition-all duration-300">
      {/* Header section */}
      <div className="flex justify-between items-start mb-5">
        <div className="flex items-start">
          <div className="p-2 bg-primary/10 rounded-lg text-primary mr-3">
            <FiActivity className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-left text-xl font-bold text-gray-800 mb-1">
              {editableWorkflow.name}
            </h3>
            <p className="text-gray-600 mb-2 leading-relaxed max-w-2xl">
              {editableWorkflow.description}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium capitalize shadow-sm ${getStatusClass(editableWorkflow.status)}`}
              >
                {editableWorkflow.status}
              </span>
              <div
                className="flex items-center text-gray-600 text-xs bg-gray-100 px-3 py-0.5 rounded-full"
                title={editableWorkflow.cadence}
              >
                <FiClock className="mr-1.5 flex-shrink-0" />
                <span className="leading-normal self-center">
                  {formatCronExpression(editableWorkflow.cadence)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <ActionButton
            icon={<FiPlay className="w-4 h-4" />}
            onClick={handleRunWorkflow}
            title="Run workflow"
            disabled={editableWorkflow.status === "running"}
          />
          <ActionButton
            icon={<FiSave className="w-4 h-4" />}
            onClick={handleSaveWorkflow}
            title="Save workflow"
            id={`save-workflow-${editableWorkflow.id}`}
          />
          <ActionButton
            icon={<FiEdit2 className="w-4 h-4" />}
            onClick={handleEditWorkflow}
            title="Edit workflow"
          />
          <ActionButton
            icon={<FiTrash2 className="w-4 h-4" />}
            onClick={handleDeleteWorkflow}
            title="Delete workflow"
            danger
          />
        </div>
      </div>

      {/* Stages section */}
      <div className="mt-6 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <FiLayers className="text-primary mr-2" />
            <h4 className="font-semibold text-gray-800">Stages</h4>
          </div>
          <button
            className="text-sm flex items-center gap-1 py-1 px-3 text-primary bg-primary/10 rounded-md hover:bg-primary/20 transition-colors"
            onClick={handleAddStage}
          >
            <FiPlus className="w-3.5 h-3.5" />
            Add Stage
          </button>
        </div>

        {editableWorkflow?.stages?.length === 0 && (
          <div className="p-8 text-center border border-dashed border-gray-300 rounded-lg bg-gray-50">
            <p className="text-gray-500">
              No stages defined yet. Click "Add Stage" to create your first
              stage.
            </p>
          </div>
        )}

        {editableWorkflow?.stages?.map((stage, stageIndex) => (
          <div key={stageIndex} className="mb-3">
            <details className="border border-gray-200 rounded-lg overflow-hidden transition-all hover:border-gray-300 group/stage">
              <summary className="p-3.5 cursor-pointer flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex justify-between items-center w-full">
                  <span className="font-medium text-gray-800 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-2 text-xs">
                      {stageIndex + 1}
                    </span>
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClass(stage.status)}`}
                    >
                      {stage.status}
                    </span>
                    <button
                      className="text-gray-400 hover:text-red-500 rounded-full p-1 hover:bg-red-500/10 transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        handleRemoveStage(stageIndex);
                      }}
                      title="Remove Stage"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <FiChevronDown className="text-gray-500 transition-transform group-open/stage:rotate-180 ml-2" />
              </summary>
              <div className="p-4 border-t border-gray-200 bg-white">
                {/* Show existing jobs */}
                {stage?.jobs?.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {stage?.jobs?.map((job, jobIndex) => (
                      <div key={job.id || jobIndex} className="relative">
                        <button
                          className="absolute top-3 right-3 z-10 text-gray-400 hover:text-red-500 rounded-full p-1 hover:bg-red-500/10 transition-colors"
                          onClick={() => handleRemoveJob(stageIndex, jobIndex)}
                          title="Remove Job"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                        <WorkflowJobs
                          jobs={[job]}
                          onJobChange={(updatedJob) =>
                            handleJobUpdate(updatedJob, jobIndex, stageIndex)
                          }
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-2">
                    No jobs in this stage.
                  </p>
                )}

                {/* Show job selector if this stage is selected */}
                {showJobSelector === stageIndex && (
                  <JobTypeSelector stageIndex={stageIndex} />
                )}

                {/* Add job button */}
                <div className="mt-4 flex justify-center">
                  <button
                    className="flex items-center gap-1.5 py-1.5 px-4 text-primary border border-primary/30 rounded-md hover:bg-primary/10 transition-colors text-sm"
                    onClick={() => setShowJobSelector(stageIndex)}
                  >
                    <FiPlus className="w-3.5 h-3.5" />
                    Add Job
                  </button>
                </div>
              </div>
            </details>
          </div>
        ))}
      </div>

      {/* Footer metadata */}
      <div className="flex justify-between pt-4 border-t border-gray-200 mt-4 text-xs text-gray-500">
        <span className="flex items-center">
          <FiCalendar className="mr-1.5" />
          Created: {formatDate(editableWorkflow.created_at)}
        </span>
        <span className="flex items-center">
          <FiInfo className="mr-1.5" />
          Updated: {formatDate(editableWorkflow.updated_at)}
        </span>
      </div>
    </div>
  );
};
